import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Flex, Image } from "@chakra-ui/react";
import { useAnimation } from "framer-motion";
import { useAuthN } from "~/hooks/useAuthN";
import ArrowImage from "~/images/yellow-curved-arrow.svg";
import { InterstitialPageContainer } from "~/OnboardingApp/components";
import { useMetaByPathConfig } from "~/OnboardingApp/features/Nav/hooks";
import { LoadingHeading, ProgressBar, YourConceivePlan } from "./components";
import { pageStates } from "./config/pageStates";

export const ThanksForSharingPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [index, setIndex] = useState(0);
  const controls = useAnimation();
  const timerRef = useRef<number | undefined>();
  const { completedNextPath } = useMetaByPathConfig();
  const {
    arrow,
    duration,
    plan,
    progress,
    text,
    transitionDuration,
    transitionStyle,
  } = pageStates[index];
  const { refreshCurrentUser } = useAuthN();

  useEffect(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
    }

    timerRef.current = window.setInterval(() => {
      setIndex((current) => {
        if (current < pageStates.length - 1) {
          return current + 1;
        } else {
          window.clearInterval(timerRef.current);
          (async () => {
            await refreshCurrentUser();
            navigate(completedNextPath, { state });
          })();
          return current;
        }
      });
    }, duration);

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [
    duration,
    controls,
    navigate,
    refreshCurrentUser,
    state,
    completedNextPath,
  ]);

  useEffect(() => {
    controls.start({
      opacity: 1,
      transition: {
        duration: transitionDuration,
        ...transitionStyle,
      },
    });
  }, [transitionDuration, transitionStyle, controls]);

  return (
    <InterstitialPageContainer>
      <Flex
        align="center"
        direction="column"
        mt={{ base: "4rem", md: "10rem" }}
        px={4}
        w="full"
      >
        <ProgressBar
          transitionDuration={transitionDuration}
          transitionStyle={transitionStyle}
          value={progress}
        />
        <Flex position="relative">
          <LoadingHeading controls={controls} text={text} />
          {arrow && (
            <Image
              alt="Arrow image"
              h="6rem"
              position="absolute"
              right={{ base: "-5rem", lg: "-6rem" }}
              src={ArrowImage}
              top={{ base: "-0.5rem", lg: "0.5rem" }}
              w="5rem"
            />
          )}
        </Flex>
        {plan && (
          <YourConceivePlan state={pageStates[index]} controls={controls} />
        )}
      </Flex>
    </InterstitialPageContainer>
  );
};
